/**
 * Take Assessment entry (v3.84.10) + the Letter/Sound row table (v3.85.0) —
 * THE list of rows the Test Type pickers offer (Dashboard and the Letter/Sound
 * page read the same table), the mapping from a row key to the page that runs
 * it, and the query parameter that page reads to preselect the row.
 *
 * v3.85.0 — Sounds variants (flag `soundsVariants`). Every Sounds row writes
 * testType 'sounds' (one test, one Results column, one NextPath measure); the
 * ROW decides the case the glyphs are drawn in (`letterCase`) and which
 * letters are preset as not administered (`preset` — the consonants-only row
 * skips a e i o u, totalLetters 21). Rows:
 *
 *   uppercase          Uppercase (letter names)
 *   lowercase          Lowercase (letter names)
 *   sounds             Sounds – Uppercase  (today's Sounds row; the key stays
 *                      'sounds' so v3.84.10 links, tab memory and tests keep
 *                      working — 'sounds_upper' is accepted as an alias)
 *   sounds_lower       Sounds – Lowercase          [flag]
 *   sounds_consonants  Sounds – Consonants only    [flag]  (lowercase, 21)
 *   sounds_both        Sounds – Both (A a)         [flag, HIDDEN — built, not
 *                      offered; enabling it later = flip `hidden`]
 *   long_vowels / short_vowels  (while the vowelTests switch is on)
 *
 * With the flag OFF the table reads exactly as v3.84.10: five rows, the Sounds
 * row labelled "Sounds" / "Letter Sounds". With it ON the Sounds row is
 * relabelled "Sounds – Uppercase" and two rows join it.
 *
 * Hand-edited URLs: a Sounds variant key that is hidden or switched off falls
 * back to Sounds – Uppercase ('sounds'); anything unknown, or a vowel key
 * while its switch is off, resolves to the default (Uppercase) — never a
 * crash, never an unreachable test. The sidebar's App.js nav map is group →
 * page (no assessment keys), so this is not a second copy of it.
 */
import { enabledVowelTestTypes, VOWEL_TEST_LABELS } from '../constants/vowelTests';
import { isFeatureEnabled } from '../constants/featureFlags';

/** Query parameter carrying the row key (the docs' `testType` name for the classic rows). */
export const TEST_TYPE_PARAM = 'testType';

export const LETTER_SOUND_ASSESSMENT_PATH = '/assessment';

/** What the Letter/Sound page runs when no key is present (old links, direct URL). */
export const DEFAULT_LETTER_SOUND_TEST = 'uppercase';

/** The Sounds row every unknown / switched-off Sounds variant falls back to. */
export const DEFAULT_SOUNDS_KEY = 'sounds';

export const SOUNDS_VARIANTS_FLAG = 'soundsVariants';

/** The vowel letters the consonants-only row presets as not administered. */
export const VOWEL_LETTERS = Object.freeze(['a', 'e', 'i', 'o', 'u']);

export const LETTER_CASES = Object.freeze(['uppercase', 'lowercase', 'both']);

/** A doc with no `letterCase` field is an uppercase run (every doc before v3.85.0). */
export const DEFAULT_LETTER_CASE = 'uppercase';

/** The vowel cards with the variants flag OFF: the pre-v3.85.0 "A a". */
export const DEFAULT_VOWEL_CASE = 'both';

/** The vowel cards with the variants flag ON: always lowercase (v3.86.2). */
export const VOWEL_CARD_CASE = 'lowercase';

const soundsVariantsOn = () => isFeatureEnabled(SOUNDS_VARIANTS_FLAG);

/**
 * THE row table. `label` is the Dashboard's short option text, `pageLabel`
 * the Letter/Sound page's; `flag` names the feature flag the row needs;
 * `hidden` rows are built and routable-by-key only through their fallback
 * (never offered). Row order = picker order.
 */
const ROWS = Object.freeze([
  { key: 'uppercase', label: 'Uppercase', pageLabel: 'Uppercase Letters', testType: 'uppercase', letterCase: 'uppercase', preset: [], hidden: false, flag: null },
  { key: 'lowercase', label: 'Lowercase', pageLabel: 'Lowercase Letters', testType: 'lowercase', letterCase: 'lowercase', preset: [], hidden: false, flag: null },
  { key: 'sounds', label: 'Sounds', pageLabel: 'Letter Sounds', flaggedLabel: 'Sounds – Uppercase', testType: 'sounds', letterCase: 'uppercase', preset: [], hidden: false, flag: null },
  { key: 'sounds_lower', label: 'Sounds – Lowercase', pageLabel: 'Sounds – Lowercase', testType: 'sounds', letterCase: 'lowercase', preset: [], hidden: false, flag: SOUNDS_VARIANTS_FLAG },
  { key: 'sounds_consonants', label: 'Sounds – Consonants only', pageLabel: 'Sounds – Consonants only', testType: 'sounds', letterCase: 'lowercase', preset: [...VOWEL_LETTERS], hidden: false, flag: SOUNDS_VARIANTS_FLAG },
  { key: 'sounds_both', label: 'Sounds – Both (A a)', pageLabel: 'Sounds – Both (A a)', testType: 'sounds', letterCase: 'both', preset: [], hidden: true, flag: SOUNDS_VARIANTS_FLAG },
  // v3.86.0: with the Sounds variants ON the two vowel rows read "Sounds – Long
  // Vowels" / "Sounds – Short Vowels" in the pickers (owner, 2026-09-30) — the
  // same flaggedLabel mechanism as the Sounds row. The doc test's own name
  // (headingForKey, Results / report columns, the NextPath feed) stays
  // "Long Vowels" / "Short Vowels".
  { key: 'long_vowels', label: VOWEL_TEST_LABELS.long_vowels, pageLabel: VOWEL_TEST_LABELS.long_vowels, flaggedLabel: 'Sounds – Long Vowels', testType: 'long_vowels', letterCase: null, preset: [], hidden: false, flag: 'vowelTests' },
  { key: 'short_vowels', label: VOWEL_TEST_LABELS.short_vowels, pageLabel: VOWEL_TEST_LABELS.short_vowels, flaggedLabel: 'Sounds – Short Vowels', testType: 'short_vowels', letterCase: null, preset: [], hidden: false, flag: 'vowelTests' },
]);

/** Legacy / alias keys → table key. */
const KEY_ALIASES = Object.freeze({ sounds_upper: 'sounds' });

const canonicalKey = (key) => (typeof key === 'string' ? (KEY_ALIASES[key] || key) : key);

const rowSwitchedOn = (row) => {
  if (row.flag === 'vowelTests') return enabledVowelTestTypes().includes(row.testType);
  if (row.flag) return isFeatureEnabled(row.flag);
  return true;
};

/** Every row in the table, offered or not (tests walk this). */
export const allLetterSoundRows = () => ROWS.map((row) => ({ ...row, preset: [...row.preset] }));

/**
 * The rows the pickers offer right now, in order: switched-on rows that are
 * not hidden. The Sounds row's and the two vowel rows' labels follow the flag
 * (the renames ship with the variants).
 */
export const letterSoundRows = () =>
  ROWS.filter((row) => !row.hidden && rowSwitchedOn(row)).map((row) => {
    const renamed = row.flaggedLabel && soundsVariantsOn();
    return {
      ...row,
      preset: [...row.preset],
      label: renamed ? row.flaggedLabel : row.label,
      pageLabel: renamed ? row.flaggedLabel : row.pageLabel,
    };
  });

/** Picker keys, in order (the v3.84.10 name — the Dashboard's selector list). */
export const letterSoundTestTypes = () => letterSoundRows().map((row) => row.key);

/** The DOC testTypes the offered rows write — what the Dashboard aggregates. */
export const letterSoundDocTestTypes = () => [...new Set(letterSoundRows().map((row) => row.testType))];

/** The table row for a key (alias-aware), offered or not; null when unknown. */
export const letterSoundRowForKey = (key) => {
  const k = canonicalKey(key);
  return ROWS.find((row) => row.key === k) || null;
};

/** Assessment key → landing route. Every table row (hidden ones too) appears here. */
export const ASSESSMENT_ENTRY_ROUTES = Object.freeze(
  Object.fromEntries(ROWS.map((row) => [row.key, LETTER_SOUND_ASSESSMENT_PATH]))
);

/** True when `key` is a row the teacher can pick today (flag-aware, alias-aware). */
export const isOfferedLetterSoundTest = (key) => letterSoundTestTypes().includes(canonicalKey(key));

/**
 * The row to run for a requested key: the key when offered; a known Sounds
 * variant that is hidden or switched off → Sounds – Uppercase; anything else
 * (absent, unknown, a vowel key while its switch is off) → the default.
 */
export const resolveLetterSoundTest = (key) => {
  const k = canonicalKey(key);
  if (isOfferedLetterSoundTest(k)) return k;
  const row = letterSoundRowForKey(k);
  if (row && row.testType === 'sounds') return DEFAULT_SOUNDS_KEY;
  return DEFAULT_LETTER_SOUND_TEST;
};

/**
 * The requested row in a `?testType=` search string — the resolved key for a
 * Sounds variant (its fallback when hidden / switched off), null when the
 * parameter is absent, unknown, or names a vowel test while the switch is
 * off. Callers treat null as "no key": the default applies.
 */
export const letterSoundTestFromSearch = (search) => {
  const key = canonicalKey(new URLSearchParams(search || '').get(TEST_TYPE_PARAM));
  if (isOfferedLetterSoundTest(key)) return key;
  const row = letterSoundRowForKey(key);
  return row && row.testType === 'sounds' ? DEFAULT_SOUNDS_KEY : null;
};

/**
 * The href Take Assessment navigates to for a row key. A key with no
 * mapping falls back to the Letter/Sound page with no key (its default) and
 * warns, rather than crashing the Dashboard — the table-driven Dashboard
 * test is what fails a missing mapping before it ships.
 */
export const takeAssessmentHref = (key) => {
  const k = canonicalKey(key);
  const path = ASSESSMENT_ENTRY_ROUTES[k];
  if (!path) {
    console.warn(`[assessmentEntry] No landing route for assessment key "${key}"; opening the default.`);
    return LETTER_SOUND_ASSESSMENT_PATH;
  }
  return `${path}?${TEST_TYPE_PARAM}=${encodeURIComponent(k)}`;
};

// ── Run specs ────────────────────────────────────────────────────────────────

/**
 * What a row RUNS: the doc testType, the glyph case and the letters preset as
 * not administered. Unknown keys run as the default row. Vowel rows carry no
 * case in the table (`letterCase: null`) — vowelCardCase() decides it.
 */
export const runSpecForKey = (key) => {
  const row = letterSoundRowForKey(resolveLetterSoundTest(key)) || letterSoundRowForKey(DEFAULT_LETTER_SOUND_TEST);
  return { key: row.key, testType: row.testType, letterCase: row.letterCase, preset: [...row.preset] };
};

/** True for the three-plus rows that write testType 'sounds'. */
export const isSoundsKey = (key) => {
  const row = letterSoundRowForKey(key);
  return !!row && row.testType === 'sounds';
};

/** The Dashboard's panel / legend heading for a row key: the doc test's name. */
export const headingForKey = (key) => {
  const row = letterSoundRowForKey(key) || letterSoundRowForKey(DEFAULT_LETTER_SOUND_TEST);
  if (row.testType === 'sounds') return 'Sounds';
  if (row.letterCase === null) return row.label;
  return row.testType.charAt(0).toUpperCase() + row.testType.slice(1);
};

/**
 * The letters a run presents: `order` (the teacher's 26-letter order for the
 * doc test, uppercase for sounds) minus the row's preset, compared
 * case-insensitively. Never a stored 21-letter order — the profile keeps 26.
 */
export const lettersForRun = (order, preset = []) => {
  const skip = new Set((preset || []).map((l) => String(l).toLowerCase()));
  return (order || []).filter((l) => !skip.has(String(l).toLowerCase()));
};

// ── Display case ─────────────────────────────────────────────────────────────

/** A valid letterCase value or the default (docs before v3.85.0 carry none). */
export const normalizeLetterCase = (value) => (LETTER_CASES.includes(value) ? value : DEFAULT_LETTER_CASE);

/** The glyph text for one letter in a case: 'uppercase' → 'M', 'lowercase' → 'm', 'both' → 'M m'. */
export const glyphForCase = (letter, letterCase) => {
  const l = String(letter);
  const c = normalizeLetterCase(letterCase);
  if (c === 'lowercase') return l.toLowerCase();
  if (c === 'both') return `${l.toUpperCase()} ${l.toLowerCase()}`;
  return l.toUpperCase();
};

/** The small case tag: 'Aa' (uppercase), 'aa' (lowercase), 'A a' (both). */
export const caseTagText = (letterCase) => {
  const c = normalizeLetterCase(letterCase);
  if (c === 'lowercase') return 'aa';
  if (c === 'both') return 'A a';
  return 'Aa';
};

/** Tooltip / long form for the tag. */
export const caseTagTitle = (letterCase) => {
  const c = normalizeLetterCase(letterCase);
  if (c === 'lowercase') return 'Given in lowercase';
  if (c === 'both') return 'Given in both cases (A a)';
  return 'Given in uppercase';
};

/**
 * The case the vowel cards show: always lowercase (owner, 2026-09-30 —
 * v3.86.2). v3.85.0 had them follow the last Sounds row picked, so a Sounds –
 * Uppercase run turned the next vowel run into capitals; that inheritance is
 * gone — no pick, page visit or tab memory changes a vowel card. With the
 * variants flag off the cards keep the pre-v3.85.0 "A a".
 */
export const vowelCardCase = () => (soundsVariantsOn() ? VOWEL_CARD_CASE : DEFAULT_VOWEL_CASE);

const ALPHABET = Object.freeze('abcdefghijklmnopqrstuvwxyz'.split(''));

/**
 * The glyphs a Letter/Sound card is SIZED against (v3.86.2): the whole
 * alphabet in the run's case — never just the letters the run presents. A
 * five-vowel run has no tall or hanging letter, so fitted on its own deck its
 * glyphs came out about 40 % larger than (and sat lower than) the same font's
 * glyphs on a 26-letter Sounds run. Sized against a–z every lowercase row —
 * Lowercase, Sounds – Lowercase, Sounds – Consonants only, the vowel tests —
 * draws one size on one baseline; the uppercase rows likewise against A–Z.
 * Alphabetical and constant, so rows of one case share one measurement.
 */
export const sizingDeckForCase = (letterCase) => ALPHABET.map((l) => glyphForCase(l, letterCase));

// ── Dashboard selection memory ───────────────────────────────────────────────
//
// The Test Type selector used to be plain component state, so every visit to
// /dashboard (Submit → sidebar → Dashboard) reset it to Uppercase. The URL
// now carries a non-default pick (`/dashboard?testType=sounds`) for refresh,
// Back and shared links, and this tab remembers the last pick so the bare
// sidebar link (`/dashboard`, no query) restores it too. sessionStorage: one
// tab's working session, never shared across tabs or devices, gone when the
// tab closes — a new day starts at Uppercase. Storage is best-effort (private
// mode / blocked storage throw): a read failure reads as "nothing remembered".
//
// v3.85.0: beside it, `dashboard.soundsCase` — the case of the last Sounds row
// the Dashboard picked, which Practice's default deck case follows. (The vowel
// cards followed it too until v3.86.2; they are always lowercase now.)
// Written only by the Dashboard, only with the variants flag on.

const DASHBOARD_TEST_TYPE_KEY = 'dashboard.testType';
const DASHBOARD_SOUNDS_CASE_KEY = 'dashboard.soundsCase';

const recall = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const remember = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    // Best-effort: the URL still carries the pick for this navigation.
  }
};

export const recallDashboardTestType = () => recall(DASHBOARD_TEST_TYPE_KEY);

export const rememberDashboardTestType = (testType) => remember(DASHBOARD_TEST_TYPE_KEY, testType);

/** The remembered Sounds case, or null (nothing remembered / storage blocked / not a case). */
export const recallDashboardSoundsCase = () => {
  const value = recall(DASHBOARD_SOUNDS_CASE_KEY);
  return LETTER_CASES.includes(value) ? value : null;
};

/**
 * Remember the case of a Sounds pick. Called by the Dashboard for every pick;
 * a non-Sounds row, or the flag off, leaves the memory untouched.
 */
export const rememberDashboardSoundsCase = (key) => {
  if (!soundsVariantsOn()) return;
  const row = letterSoundRowForKey(key);
  if (!row || row.testType !== 'sounds' || !LETTER_CASES.includes(row.letterCase)) return;
  remember(DASHBOARD_SOUNDS_CASE_KEY, row.letterCase);
};
